import type { CompensationModel } from "./workflowTypes";

export type EarningType =
  | "executor_bounty"
  | "metric_bonus"
  | "milestone_bonus"
  | "client_close"
  | "manager_override"
  | "reviewer_bonus";

export interface CalculatedEarning {
  type: EarningType;
  amountUsd: number;
  reason: string;
}

export interface CompensationInput {
  rewardUsd: number;
  compensation: CompensationModel;
  approved: boolean;
  metricValue?: number;
  milestoneApproved?: boolean;
  clientValueUsd?: number;
  managerEligible?: boolean;
  reviewerApproved?: boolean;
  discordActivityXp?: number;
}

export function calculateCompensation(input: CompensationInput): CalculatedEarning[] {
  if (!input.approved) return [];
  const base = calculateBaseAmount(input);
  const earnings: CalculatedEarning[] = [];
  if (base > 0) {
    earnings.push({
      type: baseType(input.compensation.type),
      amountUsd: roundUsd(base),
      reason: baseReason(input.compensation),
    });
  }
  const managerOverride = input.managerEligible
    ? roundUsd(base * ((input.compensation.managerOverridePercent ?? 4) / 100))
    : 0;
  if (managerOverride > 0) {
    earnings.push({
      type: "manager_override",
      amountUsd: managerOverride,
      reason: "Cell manager override from verified member output.",
    });
  }
  const reviewerBonus = input.reviewerApproved ? roundUsd(input.compensation.reviewerBonusUsd ?? 0) : 0;
  if (reviewerBonus > 0) {
    earnings.push({
      type: "reviewer_bonus",
      amountUsd: reviewerBonus,
      reason: "Reviewer bonus for approved evidence review.",
    });
  }
  return earnings;
}

export function payoutModelLabel(compensation: CompensationModel, rewardUsd: number) {
  if (compensation.type === "metric") {
    const unit = compensation.metricUnit ?? "verified unit";
    return `$${formatMoney(compensation.rateUsd ?? 0)} per ${unit}, capped at $${formatMoney(compensation.capUsd ?? rewardUsd)}`;
  }
  if (compensation.type === "client_close") {
    const percent = ((compensation.rateUsd ?? 0) * 100).toFixed(1).replace(".0", "");
    return `${percent}% of verified client value, capped at $${formatMoney(compensation.capUsd ?? rewardUsd)}`;
  }
  if (compensation.type === "milestone") return `$${formatMoney(compensation.fixedUsd ?? rewardUsd)} when the objective is approved`;
  return `$${formatMoney(compensation.fixedUsd ?? rewardUsd)} fixed bounty`;
}

function calculateBaseAmount(input: CompensationInput) {
  const cap = input.compensation.capUsd ?? input.rewardUsd;
  if (input.compensation.type === "metric") {
    return Math.min((input.metricValue ?? 0) * (input.compensation.rateUsd ?? 0), cap);
  }
  if (input.compensation.type === "milestone") {
    return input.milestoneApproved === false ? 0 : Math.min(input.compensation.fixedUsd ?? input.rewardUsd, cap);
  }
  if (input.compensation.type === "client_close") {
    const value = input.clientValueUsd ?? input.compensation.clientValueUsd ?? 0;
    return Math.min(value * (input.compensation.rateUsd ?? 0), cap);
  }
  return Math.min(input.compensation.fixedUsd ?? input.rewardUsd, cap);
}

function baseType(type: CompensationModel["type"]): EarningType {
  if (type === "metric") return "metric_bonus";
  if (type === "milestone") return "milestone_bonus";
  if (type === "client_close") return "client_close";
  return "executor_bounty";
}

function baseReason(compensation: CompensationModel) {
  if (compensation.type === "metric") return "Metric proof approved against the task SOP.";
  if (compensation.type === "milestone") return "Task milestone approved.";
  if (compensation.type === "client_close") return "Client-close value verified.";
  return "Fixed task bounty approved.";
}

function roundUsd(value: number) {
  return Math.round(value * 100) / 100;
}

function formatMoney(value: number) {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
